<script>
  import Section from '../components/Section.svelte';
  import Panel from '../components/Panel.svelte';
  import Alert from '../components/Alert.svelte';
  import Button from '../components/Button.svelte';
  import DataTable from '../components/DataTable.svelte';
  import HelpDrawer from '../components/HelpDrawer.svelte';
  import { getConfig, runQuery, saveQuery, deleteQuery } from '../lib/api.js';
  import { queryDraft } from '../lib/queryDraft.svelte.js';
  import { NS } from '../../shared/config.js';
  import { HELP } from '../lib/help.js';
  import AggregateBuilder from '../components/AggregateBuilder.svelte';
  import CompareBuilder from '../components/CompareBuilder.svelte';
  import Facets from '../components/Facets.svelte';
  import Async from '../components/Async.svelte';
  import { getSources, getDisclosure, saveAggregate, previewAggregates, deleteAggregate, getCubes, getCatalog } from '../lib/api.js';

  async function loadAggregateAuthoring() {
    const { sources } = await getSources();
    if (!sources.length) return null;
    const disclosures = await Promise.all(sources.map((source) => getDisclosure(source.id)));
    return { sources, disclosures };
  }
  const aggregateAuthoring = loadAggregateAuthoring();
  let aggregateSourceId = $state('');
  async function mint(data, aggregate) {
    const result = await saveAggregate({ sourceId: data.sourceId, aggregate, suppressBelow: data.aggregations.suppressBelow });
    data.aggregations = result.aggregations;
  }
  // There are no sample rows here — the CSV never left the analyst's browser and
  // this screen never saw it — so the counts come from the private records the
  // Pod already holds. Reads only.
  const look = (data, aggregate) => previewAggregates({ sourceId: data.sourceId, aggregate });
  async function unmint(data, aggregate) {
    const result = await deleteAggregate(data.sourceId, aggregate.id);
    data.aggregations = result.aggregations;
  }

  // What the comparison builder can reach. The catalog read is only for the
  // partner's declared measure — /api/cubes classifies their cells against ours
  // by design and so never learns theirs — and a partner we cannot read is not
  // a reason to lose the builder, hence the null fallback rather than a reject.
  const comparable = Promise.all([getCubes(), getCatalog().catch(() => null)])
    .then(([cubes, catalog]) => ({ cubes, catalog }));

  // The named queries the Pod holds. Drives the "named queries" list below, so
  // it never duplicates a hardcoded copy — the Pod is the source of truth.
  let named = $state([]);
  async function loadNamed() {
    try { named = (await getConfig('queries')).queries ?? []; }
    catch { named = []; }
  }
  loadNamed();

  // Custom-query editor. Prefilled from the Analysis "Open in custom query"
  // handoff when present, else a starter query.
  const starter = `PREFIX adr: <${NS.adr}>
PREFIX qb:  <${NS.qb}>
SELECT ?value ?period (SUM(?n) AS ?count) WHERE {
  ?o a qb:Observation ; adr:dietaryPreference ?value ; adr:period ?period ; adr:artistCount ?n .
} GROUP BY ?value ?period ORDER BY ?period DESC(?count)`;

  let custom = $state(queryDraft.sparql ?? starter);
  if (queryDraft.sparql) queryDraft.sparql = null;

  let result = $state(null);
  let error = $state(null);
  let running = $state(false);

  async function run() {
    running = true; error = null; result = null;
    try {
      result = await runQuery(custom);
    } catch (e) {
      error = e.message;
    } finally {
      running = false;
    }
  }

  // Save-as-named-query. The label becomes the panel title; theme groups it on
  // Analysis. The backend validates (runs the SPARQL once) and enforces the
  // analyst role — a save failure surfaces its message here.
  let saveLabel = $state('');
  let saveTheme = $state('');
  let saveDesc = $state('');
  let saveError = $state(null);
  let saved = $state(null);
  let saving = $state(false);
  let pendingDelete = $state(null);
  let deleting = $state(false);
  let deleteError = $state(null);

  async function save() {
    saving = true; saveError = null; saved = null;
    try {
      const entry = await saveQuery({ label: saveLabel, theme: saveTheme || null, description: saveDesc || null, sparql: custom });
      saved = entry;
      saveLabel = ''; saveTheme = ''; saveDesc = '';
      await loadNamed();
    } catch (e) {
      saveError = e.message;
    } finally {
      saving = false;
    }
  }

  function editInEditor(q) {
    custom = q.sparql;
    result = null; error = null;
    facet = 'custom';
  }

  async function remove(query) {
    deleting = true; deleteError = null;
    try {
      await deleteQuery(query.id);
      pendingDelete = null;
      await loadNamed();
    } catch (e) {
      deleteError = e.message;
    } finally {
      deleting = false;
    }
  }

  // Three separable jobs, not three steps: mint an axis the cube is missing,
  // compile a comparison, or write SPARQL by hand. Facets rather than one long
  // screen, and the panels are hidden rather than unmounted — switching away
  // from a half-built comparison and back should not have cost you the
  // comparison, and the "Edit as custom query" handoff crosses between two of
  // them.
  const FACETS = [
    { id: 'aggregates', label: 'Cubes' },
    { id: 'comparisons', label: 'Comparisons' },
    { id: 'custom', label: 'Custom queries' },
  ];
  let facet = $state('comparisons');

  // The handoff out of the comparison builder has to take the analyst with it,
  // or the compiled query lands in a textarea on a facet they cannot see.
  function handOff(sparql) {
    custom = sparql;
    result = null; error = null;
    facet = 'custom';
  }
</script>

<HelpDrawer {...HELP.queries} />

<h1 class="wf2-title wf2-title-level-1">Queries</h1>
<p class="wf2-subtitle">Write and save the queries behind your comparisons — the answers appear on Comparisons</p>

<Facets facets={FACETS} active={facet} onselect={(id) => (facet = id)} />

<!-- Hidden, not unmounted: each facet holds a draft, and switching between them
     is a normal part of building one query. -->
<div hidden={facet !== 'aggregates'}>
  <Section title="Build a missing cube">
    <Async promise={aggregateAuthoring} loading="Reading your cubes…">
      {#snippet children(authoring)}
        {#if authoring}
        <label class="wf2-field">Source for cube dimensions<select class="wf2-control" bind:value={aggregateSourceId}>
          <option value="">Choose a source…</option>
          {#each authoring.sources as source}<option value={source.id}>{source.label}</option>{/each}
        </select></label>
        {@const data = authoring.disclosures.find((item) => item.sourceId === aggregateSourceId)}
        {#if data}
        <p class="wf2-text">
          A query can only break down by something one of your cubes already
          counts. Build the missing one here — it stays private until the
          governance owner shares it.
        </p>
        <AggregateBuilder
          mapping={data.mapping} aggregateMapping={data.aggregateMapping} sourceId={data.sourceId} aggregations={data.aggregations}
          onsave={(aggregate) => mint(data, aggregate)}
          onremove={(aggregate) => unmint(data, aggregate)}
          onpreview={(aggregate) => look(data, aggregate)}
          saveLabel="Save and compute private cube" />
        {:else}<Alert title="Choose a source" variant="info"><p>The source determines which dimensions can safely coexist in a cube.</p></Alert>{/if}
        {:else}
          <Alert title="No source yet" variant="info"><p>Import and map a source before building private aggregates.</p></Alert>
        {/if}
      {/snippet}
    </Async>
  </Section>
</div>

<div hidden={facet !== 'comparisons'}>
  <Section title="Compare two cubes">
    <Async promise={comparable} loading="Reading which cubes a query can reach…">
      {#snippet children({ cubes, catalog })}
        <CompareBuilder
          {cubes} {catalog} existing={named}
          onrun={(sparql) => runQuery(sparql)}
          onsave={async (entry) => { const saved = await saveQuery(entry); await loadNamed(); return saved; }}
          onedit={handOff} />
      {/snippet}
    </Async>
  </Section>
</div>

<div hidden={facet !== 'custom'}>
  <Section title="Custom query">
    <Panel>
      <p class="wf2-text">
        You write the question; the app decides what it is allowed to read. A
        query here can only ever reach the shared cubes this institution already
        has access to — never your private records, however it is written.
      </p>
      <textarea class="wf2-control mono" rows="9" bind:value={custom}></textarea>
      <div class="wf2-row" style="gap:12px; margin-top: var(--space-2)">
        <Button variant="primary" onclick={run} disabled={running}>{running ? 'Running…' : 'Run query'}</Button>
        <Button variant="secondary" to="analysis">Comparisons</Button>
      </div>

      {#if error}
        <Alert title="Query error" variant="danger"><p class="mono">{error}</p></Alert>
      {:else if result}
        {#if result.rows.length}
          <DataTable columns={result.columns} rows={result.rows.map((r) => result.columns.map((c) => r[c]))} />
        {:else}
          <p class="muted">No rows.</p>
        {/if}
      {/if}
    </Panel>
  </Section>

  <Section title="Save this query">
    <Panel>
      {#if saved}
        <Alert title="Saved to your Pod" variant="success">
          <p class="wf2-text">
            "{saved.label}" is saved. <a href="#/analysis">See it on Comparisons →</a>
          </p>
        </Alert>
      {:else}
        <div class="wf2-row" style="gap:12px; flex-wrap:wrap">
          <input class="wf2-control" style="flex:2 1 200px" placeholder="Query name (e.g. Nationality distribution)" bind:value={saveLabel} />
          <input class="wf2-control" style="flex:1 1 120px" placeholder="Theme (optional)" bind:value={saveTheme} />
        </div>
        <input class="wf2-control" style="margin-top: var(--space-2)" placeholder="Description (optional)" bind:value={saveDesc} />
        <div class="wf2-row" style="gap:12px; margin-top: var(--space-2)">
          <Button variant="primary" onclick={save} disabled={saving || !saveLabel}>{saving ? 'Saving…' : 'Save query'}</Button>
        </div>
        {#if saveError}
          <Alert title="Could not save" variant="danger"><p class="mono">{saveError}</p></Alert>
        {/if}
      {/if}
    </Panel>
  </Section>
</div>

<Section title="Saved queries">
  <Panel>
      {#if pendingDelete}
        <Alert title={`Delete “${pendingDelete.label}”?`} variant="warning">
          <p>This removes the saved query from your Pod and from Comparisons. Its cubes and source data are not changed.</p>
          <div class="wf2-row" style="gap:12px">
            <Button variant="primary" disabled={deleting} onclick={() => remove(pendingDelete)}>{deleting ? 'Deleting…' : 'Delete query'}</Button>
            <Button variant="ghost" disabled={deleting} onclick={() => (pendingDelete = null)}>Keep it</Button>
          </div>
        </Alert>
      {/if}
      {#if deleteError}
        <Alert title="Could not delete query" variant="danger"><p class="mono">{deleteError}</p></Alert>
      {/if}
      {#if named.length}
        <div class="wf2-tablewrap" style="overflow-x:auto">
          <table class="wf2-table">
            <thead><tr><th>Query</th><th>Theme</th><th>Description</th><th></th></tr></thead>
            <tbody>
              {#each named as q}
                <tr>
                  <td>{q.label}</td>
                  <td>{#if q.theme}<span class="tag {q.theme}">{q.theme}</span>{/if}</td>
                  <td class="wf2-caption">{q.description ?? ''}</td>
                  <td style="white-space:nowrap">
                    <Button variant="ghost" onclick={() => editInEditor(q)}>Load</Button>
                    <Button variant="ghost" onclick={() => { pendingDelete = q; deleteError = null; }}>Delete</Button>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {:else}
        <p class="muted">You haven't saved any queries yet. Write one above and save it.</p>
      {/if}
  </Panel>
</Section>
